import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { USER_OPERATIONS, RESOURCE_TYPE, MESSAGE_TYPE } from "../../constants";
import defaultStyles from "./LineFeature.module.css";
import "../../cssModules/switch.css";
import { usePlantAndLinesStore } from "../../providers/PlantAndLinesStore/PlantAndLinesStore";
import { withAllowedOperationsProvider } from "../../hocs";
import Table from "../Table/Table";
import LineFeatureToolbar from "../LineFeatureToolbar/LineFeatureToolbar";
import { getLineFeaturePerPlant, toggleLineFeature } from "../../apis/calls";
import { usePopupManager } from "../../providers/PopupManager/PopupManager";

const LineFeature = ({
  setHasActiveEdits,
  hasActiveEdits,
  handleCountChange,
}) => {
  const ROW_HEIGHT = 38;

  const RESOURCE_OPERATION = USER_OPERATIONS.UPDATE_LINE_FEATURE; 

  const { showError, showInternalError, showPopup } = usePopupManager();

  const [plant] = usePlantAndLinesStore().plantState;

  const [styles] = useState(defaultStyles);

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });

  const [recordCount, setRecordCount] = useState(0);

  const setCount = (count) => {
    setRecordCount(count);
  };

  const fixedColumns = [
    {
      width: 100,
      title: "Plant",
      field: "plant",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "Plant Id",
      field: "plantId",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "Line Name",
      field: "lineName",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: "Line Id",
      field: "line",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "Shop Type",
      field: "shopType",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 320,
      title: "Feature Name",
      field: "featureName",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 100,
      title: "Active",
      field: "activeText",
      render: ({ activeText }) => (
        <span
          className={
            activeText === "ENABLED" ? styles.activeTrue : styles.activeFalse
          }
        >
          {activeText}
        </span>
      ),
      enableSearch: true,
      enableFilter: true,
    },
  ].filter((c) => c);

  const getLineFeatureData = async () => {
    if (plant.value) {
      setRowsData((prev) => ({
        ...prev,
        loading: true,
      }));

      try {
        const response = await getLineFeaturePerPlant(plant.value);

        const updatedData = response.data.map((row) => ({
          ...row,
          activeText: row.active ? "ENABLED" : "DISABLED",
        }));

        setRowsData({
          loading: false,
          data: updatedData,
          filteredData: updatedData,
        });
      } catch (err) {
        showInternalError({ error: err });
      }
    }
  };

  const handleDisable = async (selectedRows) => {
    if (!selectedRows.length) {
      showError({
        contextText: "No Rows Selected",
        info: "Please select at least one Line Feature.",
      });
      return;
    }

    try {
      const payload = selectedRows.map((row) => ({
        id: row.id,
        active: false,
      }));

      await toggleLineFeature(payload);

      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: "Line Feature updated successfully.",
        info: "Selected Lines have been disabled.",
      });

      getLineFeatureData();
    } catch (error) {
      showError({
        contextText: "Disable Failed",
        info: "Unable to disable Line Feature.",
      });
    }
  };

  const handleEnable = async (selectedRows) => {
    if (!selectedRows.length) {
      showError({
        contextText: "No Rows Selected",
        info: "Please select at least one Line Feature.",
      });
      return;
    }

    try {
      const payload = selectedRows.map((row) => ({
        id: row.id,
        active: true,
      }));

      await toggleLineFeature(payload);

      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: "Line Feature updated successfully.",
        info: "Selected Lines have been enabled.",
      });

      getLineFeatureData();
    } catch (error) {
      showError({
        contextText: "Enable Failed",
        info: "Unable to enable Line Feature.",
      });
    }
  };

  useEffect(() => {
    handleCountChange(recordCount);
  }, [recordCount, handleCountChange]);

  useEffect(() => {
    setHasActiveEdits(rowsData.data.some((row) => row.edited));
  }, [rowsData.data, setHasActiveEdits]);

  useEffect(() => {
    if (plant.value) {
      getLineFeatureData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value]);

  return (
    <>
      <LineFeatureToolbar plantName={plant.label} />

      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: "100%" }}>
          <Table
            columns={fixedColumns}
            defaultColumns={[...fixedColumns]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.filteredData}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={setCount}
            isDataLoading={rowsData.loading}
            secondaryActions={[
              {
                name: "DISABLE",
                actionFn: handleDisable,
                authOperation: RESOURCE_OPERATION,
                backgroundColor: "#dc3545",
              },
              {
                name: "ENABLE",
                actionFn: handleEnable,
                authOperation: RESOURCE_OPERATION,
              },
            ]}
          />
        </div>
      </div>
    </>
  );
};

LineFeature.propTypes = {
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
};

export default withAllowedOperationsProvider(
  LineFeature,
  false,
  RESOURCE_TYPE.MASTER_CAPACITY, 
);
